import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { StackLayout } from '@euler/app/components/StackLayout';
import { useOrderContext } from '@euler/app/flows/order/functions/OrderContext';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { ChildTaskListItem } from '@euler/app/flows/task/pages/detail/components/ChildTaskListItem';
import { Card } from '@euler/components/Card';
import { StatusColors } from '@euler/components/Colors';
import { EmptyView } from '@euler/components/EmptyView';
import { LayoutProviderView } from '@euler/components/layout/LayoutProvider';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useAdaptiveContainerWidth } from '@euler/functions/useAdaptiveContainerWidth';
import { VehicleInspectionTask } from '@euler/model/entity';
import { safeMarkDevFlag } from '@euler/utils';
import { memo, useCallback, useEffect } from 'react';
import ContentLoader, {
  Circle,
  IContentLoaderProps,
  Rect,
} from 'react-content-loader/native';
import { StyleProp, View, ViewStyle } from 'react-native';

const kForceLoading = safeMarkDevFlag(false);
const kForceError = safeMarkDevFlag(false);

export const ChildTaskList = memo(
  ({ style }: { style?: StyleProp<ViewStyle> }) => {
    const { rpx } = useResponsiveContext();
    const {
      taskNo,
      focused$,
      asyncChildTasks: state,
      fetchChildTasks: fetch,
    } = useTaskContext();
    const appNav = useAppNavigation();
    const { event$ } = useOrderContext();

    const onViewDetail = useCallback(
      (task: VehicleInspectionTask) => {
        appNav.push('Task', {
          taskNo: task.taskNo,
        });
      },
      [appNav],
    );

    useEffect(() => {
      const sub = event$.subscribe(event => {
        if (
          event.type === 'created' &&
          !event.isMainOrder &&
          event.mainTaskNo === taskNo
        ) {
          void fetch();
        }
      });
      return () => sub.unsubscribe();
    }, [event$, fetch, taskNo]);

    useEffect(() => {
      const sub = focused$.subscribe(() => {
        void fetch();
      });
      return () => sub.unsubscribe();
    }, [fetch, focused$]);

    if (
      ((!('result' in state) || !state.result) &&
        (state.status === 'idle' || state.status === 'loading')) ||
      kForceLoading
    ) {
      return (
        <LayoutProviderView
          css={`
            margin-bottom: ${rpx(15)}px;
          `}
        >
          <StackLayout style={style} gap={rpx(15)}>
            <ChildTaskListContentLoader />
            <ChildTaskListContentLoader />
            <ChildTaskListContentLoader />
          </StackLayout>
        </LayoutProviderView>
      );
    }

    if (state.status === 'error' || kForceError) {
      return (
        <Card
          style={style}
          css={`
            margin-bottom: ${rpx(15)}px;
          `}
        >
          <EmptyView
            error={
              kForceError || !('error' in state)
                ? new Error('504 Gateway Timeout')
                : state.error
            }
            message={`
            加载子订单信息失败，请稍后重试！\n(${
              'error' in state ? state.error?.message : '504 Gateway Timeout'
            })
          `}
            onRetry={fetch}
            buttonStyle={{
              height: rpx(32),
              minWidth: 100,
              backgroundColor: StatusColors.Danger,
              borderRadius: 0,
              marginTop: 0,
            }}
            buttonTextStyle={{
              color: 'white',
              fontSize: rpx(15),
            }}
            messageStyle={{
              fontFamily: FontFamily.NotoSans.Medium,
              marginTop: 0,
            }}
          />
        </Card>
      );
    }

    return (
      <StackLayout
        gap={rpx(15)}
        style={style}
        css={`
          margin-bottom: ${rpx(15)}px;
        `}
      >
        {state.result?.map(task => (
          <ChildTaskListItem
            key={task.id}
            task={task}
            onViewDetail={onViewDetail}
          />
        ))}
      </StackLayout>
    );
  },
);

export const ChildTaskListContentLoader = memo(
  ({
    style,
    ...props
  }: IContentLoaderProps & { style?: StyleProp<ViewStyle> }) => {
    const { rpx } = useResponsiveContext();
    const width = useAdaptiveContainerWidth();
    const loaderWidth = width - rpx(80);
    const loaderHeight = loaderWidth * (124 / 425);
    return (
      <View
        style={style}
        css={`
          background-color: #fff;
          padding: ${rpx(20)}px ${rpx(15)}px 0;
          border-radius: ${rpx(10)}px;
          justify-content: center;
        `}
      >
        <ContentLoader
          speed={2}
          width={loaderWidth}
          height={loaderHeight}
          viewBox="0 0 425 124"
          backgroundColor="#d7d7d7"
          foregroundColor="#ecebeb"
          {...props}
        >
          <Rect x="48" y="8" rx="3" ry="3" width="88" height="6" />
          <Rect x="48" y="26" rx="3" ry="3" width="52" height="6" />
          <Rect x="0" y="56" rx="3" ry="3" width="410" height="6" />
          <Rect x="0" y="72" rx="3" ry="3" width="380" height="6" />
          <Rect x="0" y="88" rx="3" ry="3" width="178" height="6" />
          <Circle cx="20" cy="20" r="20" />
        </ContentLoader>
      </View>
    );
  },
);
